import { useEffect, useMemo, useRef } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { ArrowUpRightIcon } from '../components/ArrowUpRightIcon'
import { LINKS } from '../config/links'
import { useStations } from '../data/catalog'
import { Seo } from '../seo/Seo'
import {
  mobileAppsSchema,
  organizationSchema,
  radioServiceSchema,
  webAppSchema,
  websiteSchema,
} from '../seo/schema'
import { SITE } from '../seo/site'
import './Home.css'
import './Stations.css'

type ChromeContext = {
  openGetApp: () => void
}

export function Home() {
  const { openGetApp } = useOutletContext<ChromeContext>()
  const phoneRef = useRef<HTMLImageElement>(null)
  const mainRef = useRef<HTMLElement>(null)

  const stations = useStations()
  const featured = useMemo(() => stations.slice(0, 6), [stations])

  // Scroll: phone parallax (rise + scale)
  useEffect(() => {
    const onScroll = () => {
      if (!phoneRef.current) return
      const progress = Math.min(window.scrollY / window.innerHeight, 1)
      const scale = 1 + progress * 0.06
      const translateY = progress * -40
      phoneRef.current.style.transform = `scale(${scale}) translateY(${translateY}px)`
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Scroll reveal: elements with [data-reveal] animate in when visible
  useEffect(() => {
    const els = mainRef.current?.querySelectorAll<HTMLElement>('[data-reveal]')
    if (!els?.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement
            const delay = el.dataset.delay ?? '0'
            el.style.transitionDelay = `${delay}ms`
            el.classList.add('is-visible')
            observer.unobserve(el)
          }
        })
      },
      { threshold: 0.12 }
    )

    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [featured])

  return (
    <main className="home" ref={mainRef}>
      <Seo
        description={SITE.defaultDescription}
        path="/"
        jsonLd={[
          websiteSchema(),
          organizationSchema(),
          radioServiceSchema(),
          webAppSchema(),
          ...mobileAppsSchema(),
        ]}
      />

      {/* ── Hero ── */}
      <section className="home__hero shell">
        <div className="home__copy">
          <h1 className="home__headline">Just Hit<br />Play.</h1>
          <p className="home__sub" style={{ animationDelay: '200ms' }}>
            Yiddish, kumzitz, chazzanus, and new Jewish music — live, free, 24/6.
          </p>
          <div className="home__actions" style={{ animationDelay: '320ms' }}>
            <a
              className="btn btn--primary"
              href={LINKS.webApp}
              target="_blank"
              rel="noopener noreferrer"
            >
              Launch web app
              <ArrowUpRightIcon />
            </a>
            <button type="button" className="btn btn--ghost" onClick={openGetApp}>
              Get the app
            </button>
          </div>
        </div>

        <div className="home__visual" aria-hidden>
          <img
            ref={phoneRef}
            className="home__phone"
            src="/hero-phone.png"
            alt=""
            draggable="false"
          />
        </div>
      </section>

      {/* ── Stations ── */}
      <section className="home__stations-section shell" aria-label="Popular stations">
        <div className="home__stations-head" data-reveal data-delay="0">
          <h2 className="home__stations-title">Your vibe, your station.</h2>
        </div>

        <ul className="home__stations-grid">
          {featured.map((station, i) => (
            <li
              key={station.slug}
              className="home__station-item"
              data-reveal
              data-delay={i * 60}
            >
              <Link
                className="stations__link"
                to={`/stations/${station.slug}`}
                aria-label={`${station.name} Jewish music radio`}
              >
                <img src={station.icon} alt="" width={88} height={88} loading="lazy" />
                <h3>{station.name}</h3>
              </Link>
            </li>
          ))}
        </ul>

        <div className="home__stations-footer" data-reveal data-delay="100">
          <Link to="/stations" className="btn btn--primary">
            View all stations <ArrowUpRightIcon />
          </Link>
        </div>
      </section>

      {/* ── It's Free ── */}
      <section className="home__free shell">
        <p className="home__free-tag" data-reveal data-delay="0">No subscription. No ads.</p>
        <h2 className="home__free-headline" data-reveal data-delay="80">It's Free.</h2>
        <button
          type="button"
          className="btn btn--primary home__free-btn"
          onClick={openGetApp}
          data-reveal
          data-delay="180"
        >
          Get the app
        </button>
      </section>

      <p className="home__crawl" aria-hidden="true">
        Music24 is free Jewish music radio: stream Yiddish songs, sefira vocals, kumzitz, chazzanus, simcha sets, and new music as the catalog changes, with no account, no subscription, and the stream pauses for Shabbos. Every station has its own page from the live catalog — Jewish music, Yiddish music, sefira music, Shabbos, streaming, and new music — on air now: {stations.map((station) => station.name).join(', ')}.
      </p>
    </main>
  )
}
